import {
  isSpan,
  isTextBlock,
  type PortableTextBlock,
  type PortableTextSpan,
} from '@portabletext/schema'
import {serializePath} from '../../paths/serialize-path'
import {resolveContainerAt} from '../../schema/resolve-container-at'
import {getNodeChildren} from '../../traversal/get-children'
import {getNode} from '../../traversal/get-node'
import {isKeyedSegment} from '../../utils/util.is-keyed-segment'
import type {Editor} from '../interfaces/editor'
import type {Node} from '../interfaces/node'
import type {Path} from '../interfaces/path'

export function insertChildren<T>(
  xs: T[],
  index: number,
  ...newValues: T[]
): T[] {
  const result = xs.slice()
  result.splice(index, 0, ...newValues)
  return result
}

function replaceChildren<T>(
  xs: T[],
  index: number,
  removeCount: number,
  ...newValues: T[]
): T[] {
  const result = xs.slice()
  result.splice(index, removeCount, ...newValues)
  return result
}

export const removeChildren = replaceChildren

/**
 * Find the index of a node in an array by its _key.
 */
function findIndexByKey(children: Array<Node>, key: string): number {
  return children.findIndex((child) => child._key === key)
}

/**
 * Extract the keyed segments from a path (skipping field name strings).
 */
function getKeyedSegments(path: Path): Array<{_key: string}> {
  return path.filter(isKeyedSegment)
}

/**
 * Replace a descendant with a new node, replacing all ancestors
 */
export const modifyDescendant = <N extends Node>(
  editor: Editor,
  path: Path,
  f: (node: N) => N,
): void => {
  if (path.length === 0) {
    return
  }

  const context = {
    schema: editor.snapshot.context.schema,
    containers: editor.snapshot.context.containers,
  }
  const nodeEntry = getNode(editor.snapshot, path)
  if (!nodeEntry) {
    return
  }
  const node = nodeEntry.node
  let modifiedNode: Node = f(node as N)

  // Walk down from the root to collect the child field name at each level.
  // This is needed to rebuild ancestors using the correct field (e.g. 'rows',
  // 'cells', 'content') instead of always assuming 'children'.
  // Use the resolved path from getNode which converts numeric segments to keyed.
  // Also track original numeric indices for segments that were numeric in the input path,
  // since keyed lookup is ambiguous for duplicate keys.
  const keyedSegments = getKeyedSegments(nodeEntry.path)
  const numericIndices = new Map<number, number>()
  {
    let keyedIdx = 0
    for (const segment of path) {
      if (typeof segment === 'number') {
        numericIndices.set(keyedIdx, segment)
        keyedIdx++
      } else if (isKeyedSegment(segment)) {
        keyedIdx++
      }
    }
  }
  const fieldNames: string[] = []
  {
    let currentNode: Node | {value: Array<Node>} = {
      value: editor.snapshot.context.value,
    }
    let currentParent:
      | import('../../schema/resolve-containers').RegisteredContainer
      | undefined

    for (let i = 0; i < keyedSegments.length; i++) {
      const result = getNodeChildren(context, currentNode, currentParent)
      if (!result) {
        return
      }
      fieldNames.push(result.fieldName)
      // Use numeric index when available (handles duplicate keys where
      // keyed lookup would find the wrong sibling).
      const numericIndex = numericIndices.get(i)
      const child =
        numericIndex !== undefined
          ? result.children[numericIndex]
          : result.children.find((c) => c._key === keyedSegments[i]!._key)
      if (!child) {
        return
      }
      currentNode = child
      currentParent = result.parent
    }
  }

  // Rebuild ancestors from the target node back up to the root.
  // keyedSegments[0] is the top-level block, keyedSegments[last] is the target.
  // We walk backwards, replacing each ancestor's child with the modified node.
  for (let level = keyedSegments.length - 2; level >= 0; level--) {
    const childKey = keyedSegments[level + 1]!._key
    const fieldName = fieldNames[level + 1]!

    // Build ancestor path from the original path by finding the segment
    // at the given keyed level. We count non-string segments to map from
    // keyed level to original path index.
    let ancestorPathEnd = -1
    let keyedCount = 0
    for (let pi = 0; pi < path.length; pi++) {
      if (typeof path[pi] !== 'string') {
        if (keyedCount === level) {
          ancestorPathEnd = pi + 1
          break
        }
        keyedCount++
      }
    }
    const ancestorPath =
      ancestorPathEnd > 0 ? path.slice(0, ancestorPathEnd) : []

    const ancestorEntry = getNode(editor.snapshot, ancestorPath)
    if (!ancestorEntry) {
      return
    }
    const ancestorNode = ancestorEntry.node
    const ancestorRecord = ancestorNode as Record<string, unknown>
    const currentChildren = Array.isArray(ancestorRecord[fieldName])
      ? (ancestorRecord[fieldName] as Node[])
      : []

    // Use numeric index if the original path had a number at this level,
    // since keyed lookup is ambiguous for duplicate keys.
    const originalNumericIndex = numericIndices.get(level + 1)
    const childIndex =
      originalNumericIndex !== undefined
        ? originalNumericIndex
        : findIndexByKey(currentChildren, childKey)
    if (childIndex === -1) {
      return
    }

    modifiedNode = {
      ...ancestorNode,
      [fieldName]: replaceChildren(
        currentChildren,
        childIndex,
        1,
        modifiedNode,
      ),
    }
  }

  let rootIndex: number
  const rootNumericIndex = numericIndices.get(0)
  if (rootNumericIndex !== undefined) {
    rootIndex = rootNumericIndex
  } else if (keyedSegments.length > 0) {
    const rootSegment = keyedSegments[0]!
    const mapIndex = editor.blockIndexMap.get(serializePath([rootSegment]))
    rootIndex =
      mapIndex !== undefined
        ? mapIndex
        : findIndexByKey(editor.snapshot.context.value, rootSegment._key)
  } else {
    const firstSegment = path[0]
    rootIndex = typeof firstSegment === 'number' ? firstSegment : -1
  }
  if (rootIndex === -1 || rootIndex >= editor.snapshot.context.value.length) {
    return
  }
  editor.snapshot.context.value = replaceChildren(
    editor.snapshot.context.value,
    rootIndex,
    1,
    modifiedNode,
  ) as PortableTextBlock[]
}

/**
 * Replace the children of a node, replacing all ancestors.
 *
 * When path is empty, modifies the editor's children directly.
 * When path points to a node, resolves the correct child field name
 * via getNodeChildren and modifies that field.
 */
export const modifyChildren = (
  editor: Editor,
  path: Path,
  f: (children: Node[]) => Node[],
) => {
  if (path.length === 0) {
    editor.snapshot.context.value = f(
      editor.snapshot.context.value,
    ) as PortableTextBlock[]
  } else {
    const context = {
      schema: editor.snapshot.context.schema,
      containers: editor.snapshot.context.containers,
    }

    const nodeEntry = getNode(editor.snapshot, path)
    if (!nodeEntry) {
      return
    }

    // Resolve this node's positional container entry directly so the
    // correct `field` is used regardless of whether `_type` is
    // registered globally or only as a positional override.
    const resolved = resolveContainerAt(
      context.containers,
      editor.snapshot.context.value,
      path,
    )
    let fieldName: string
    if (resolved && 'field' in resolved) {
      fieldName = resolved.field.name
    } else if (isTextBlock(context, nodeEntry.node)) {
      fieldName = 'children'
    } else {
      fieldName = 'children'
    }

    modifyDescendant(editor, path, (node) => {
      const record = node as Record<string, unknown>
      const currentChildren = Array.isArray(record[fieldName])
        ? (record[fieldName] as Node[])
        : []
      return {
        ...node,
        [fieldName]: f(currentChildren),
      }
    })
  }
}

/**
 * Replace a leaf, replacing all ancestors
 */
export const modifyLeaf = (
  editor: Editor,
  path: Path,
  f: (leaf: PortableTextSpan) => PortableTextSpan,
) =>
  modifyDescendant(editor, path, (node) => {
    if (!isSpan({schema: editor.snapshot.context.schema}, node)) {
      return node
    }

    return f(node)
  })
